import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { CrmInteractionDTO, CrmTaskDTO, CrmLeadDTO, CrmDashboardSummaryDTO } from '../types';

interface CrmStore {
    // Interactions
    interactions: CrmInteractionDTO[];
    selectedInteraction: CrmInteractionDTO | null;
    interactionTotal: number;

    // Tasks
    tasks: CrmTaskDTO[];
    selectedTask: CrmTaskDTO | null;
    taskTotal: number;

    // Leads
    leads: CrmLeadDTO[];
    selectedLead: CrmLeadDTO | null;
    leadTotal: number;

    // Dashboard
    dashboardSummary: CrmDashboardSummaryDTO | null;

    // UI state
    isLoading: boolean;
    isSubmitting: boolean;
    error: string | null;

    // Actions
    setInteractions: (items: CrmInteractionDTO[], total: number) => void;
    setSelectedInteraction: (item: CrmInteractionDTO | null) => void;
    setTasks: (items: CrmTaskDTO[], total: number) => void;
    setSelectedTask: (item: CrmTaskDTO | null) => void;
    setLeads: (items: CrmLeadDTO[], total: number) => void;
    setSelectedLead: (item: CrmLeadDTO | null) => void;
    setDashboardSummary: (summary: CrmDashboardSummaryDTO) => void;
    setLoading: (loading: boolean) => void;
    setSubmitting: (submitting: boolean) => void;
    setError: (error: string | null) => void;
    reset: () => void;
}

const initialState = {
    interactions: [] as CrmInteractionDTO[],
    selectedInteraction: null as CrmInteractionDTO | null,
    interactionTotal: 0,
    tasks: [] as CrmTaskDTO[],
    selectedTask: null as CrmTaskDTO | null,
    taskTotal: 0,
    leads: [] as CrmLeadDTO[],
    selectedLead: null as CrmLeadDTO | null,
    leadTotal: 0,
    dashboardSummary: null as CrmDashboardSummaryDTO | null,
    isLoading: false,
    isSubmitting: false,
    error: null as string | null,
};

export const useCrmStore = create<CrmStore>()(
    devtools(
        (set) => ({
            ...initialState,
            setInteractions: (items, total) => set({ interactions: items, interactionTotal: total }),
            setSelectedInteraction: (item) => set({ selectedInteraction: item }),
            setTasks: (items, total) => set({ tasks: items, taskTotal: total }),
            setSelectedTask: (item) => set({ selectedTask: item }),
            setLeads: (items, total) => set({ leads: items, leadTotal: total }),
            setSelectedLead: (item) => set({ selectedLead: item }),
            setDashboardSummary: (summary) => set({ dashboardSummary: summary }),
            setLoading: (loading) => set({ isLoading: loading }),
            setSubmitting: (submitting) => set({ isSubmitting: submitting }),
            setError: (error) => set({ error }),
            reset: () => set(initialState),
        }),
        { name: 'crm-store' }
    )
);
